import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, GitPullRequestArrow, Trash2, Wand2, Play, Sparkles, Receipt, Star as StarIcon, Shield, Paperclip } from "lucide-react";

const FIELDS = [
  { value: "sender_email", label: "Sender email" },
  { value: "sender_domain", label: "Sender domain" },
  { value: "recipient", label: "Recipient" },
  { value: "subject_contains", label: "Subject contains" },
  { value: "body_contains", label: "Body contains" },
  { value: "category", label: "AI category" },
  { value: "priority", label: "Priority" },
  { value: "ai_sentiment", label: "AI sentiment" },
  { value: "fraud_score", label: "Fraud score" },
  { value: "has_attachment", label: "Has attachment" },
  { value: "tags_contains", label: "Tag" },
];

const OPS = [
  { value: "equals", label: "equals" },
  { value: "contains", label: "contains" },
  { value: "gt", label: ">" },
  { value: "lt", label: "<" },
  { value: "regex", label: "matches regex" },
];

const ACTIONS = [
  { value: "move_to_folder", label: "Move to folder", needsFolder: true },
  { value: "star", label: "Star (deep blue)" },
  { value: "mark_read", label: "Mark as read" },
  { value: "assign_department", label: "Assign department", needsDept: true },
  { value: "set_priority", label: "Set priority", options: ["low", "normal", "high", "urgent"] },
  { value: "add_tag", label: "Add tag" },
  { value: "create_ticket", label: "Create ticket" },
  { value: "run_ai_agent", label: "Run AI agent" },
  { value: "mark_spam", label: "Mark as spam" },
  { value: "archive", label: "Archive" },
  { value: "block_auto_reply", label: "Block AI auto-reply" },
];

const PRESET_ICONS = { receipt: Receipt, star: StarIcon, shield: Shield, paperclip: Paperclip, sparkles: Sparkles };

export default function Rules() {
  const { activeCompanyId } = useAuth();
  const [rules, setRules] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [depts, setDepts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [presets, setPresets] = useState([]);
  const [presetOpen, setPresetOpen] = useState(false);
  const [installTarget, setInstallTarget] = useState(null); // preset being configured before install
  const [open, setOpen] = useState(false);
  const emptyForm = { company_id: "", account_id: "", name: "", match_type: "all", stop_processing: false,
    conditions: [{ field: "sender_domain", op: "contains", value: "" }],
    actions: [{ type: "move_to_folder", value: "" }] };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [r, c, d, a, f, p] = await Promise.all([
      api.get("/rules", { params }),
      api.get("/companies"),
      api.get("/departments", { params }),
      api.get("/email-accounts", { params }),
      api.get("/mail-folders", { params }),
      api.get("/rules/presets"),
    ]);
    setRules(r.data); setCompanies(c.data); setDepts(d.data); setAccounts(a.data); setFolders(f.data); setPresets(p.data);
    if (!form.company_id) setForm((v) => ({ ...v, company_id: activeCompanyId || c.data[0]?.id || "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const companyFolders = useMemo(() => folders.filter((x) => x.company_id === form.company_id), [folders, form.company_id]);
  const companyAccounts = useMemo(() => accounts.filter((x) => x.company_id === form.company_id), [accounts, form.company_id]);
  const companyDepts = useMemo(() => depts.filter((x) => x.company_id === form.company_id), [depts, form.company_id]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, account_id: form.account_id || null };
      await api.post("/rules", payload);
      toast.success("Rule created");
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (r) => { await api.patch(`/rules/${r.id}`, { active: !r.active }); load(); };
  const remove = async (id) => {
    if (!window.confirm("Delete rule?")) return;
    await api.delete(`/rules/${id}`); toast.success("Deleted"); load();
  };
  const runNow = async (r) => {
    try {
      const { data } = await api.post(`/rules/${r.id}/run-now`);
      toast.success(`Matched ${data.matched}, updated ${data.updated}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateCondition = (idx, field, value) => {
    const conditions = [...form.conditions];
    conditions[idx] = { ...conditions[idx], [field]: value };
    setForm({ ...form, conditions });
  };
  const updateAction = (idx, field, value) => {
    const actions = [...form.actions];
    actions[idx] = { ...actions[idx], [field]: value };
    setForm({ ...form, actions });
  };

  const installPreset = async () => {
    if (!installTarget) return;
    const p = installTarget.preset;
    try {
      const body = { company_id: form.company_id, preset_key: p.key, account_id: installTarget.account_id || null, folder_id: installTarget.folder_id || null };
      await api.post("/rules/from-preset", body);
      toast.success(`"${p.name}" installed`);
      setInstallTarget(null); setPresetOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Install failed"); }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="rules-page">
      <PageHeader
        title="Routing rules"
        subtitle="If-this-then-that automations for incoming email. Move to folders, star VIPs, assign departments, block AI auto-replies."
        eyebrow="Automation"
        actions={
          <div className="flex items-center gap-2">
            <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="preset-rules-button"><Wand2 className="h-4 w-4 mr-1.5" /> From preset</Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-2xl" data-testid="preset-rules-modal">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-terracotta" /> Install a preset rule
                  </DialogTitle>
                  <DialogDescription>Ready-made automations you can install with one click.</DialogDescription>
                </DialogHeader>
                {!installTarget && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-testid="preset-list">
                    {presets.map((p) => {
                      const Icon = PRESET_ICONS[p.icon] || Sparkles;
                      return (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => setInstallTarget({ preset: p, folder_id: "", account_id: "" })}
                          className="text-left border rounded-sm p-3 hover:shadow-md hover:-translate-y-0.5 transition-all hover:border-emerald-brand/40"
                          data-testid={`preset-rule-${p.key}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-sm bg-terracotta/10 text-terracotta grid place-items-center">
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="font-medium text-sm">{p.name}</div>
                          </div>
                          <div className="text-xs text-muted-foreground mt-2 line-clamp-3">{p.description}</div>
                          {p.requires_folder && (
                            <div className="text-[10px] font-mono uppercase text-muted-foreground mt-1.5">
                              Requires: {p.requires_folder.name} folder
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {installTarget && (
                  <div className="space-y-3 mt-3" data-testid="preset-install-form">
                    <div className="border border-border rounded-sm p-3 bg-muted/20">
                      <div className="font-medium text-sm">{installTarget.preset.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{installTarget.preset.description}</div>
                    </div>
                    <div>
                      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Scope (leave blank = all mailboxes in company)</Label>
                      <Select value={installTarget.account_id || "__all__"} onValueChange={(v) => setInstallTarget({ ...installTarget, account_id: v === "__all__" ? "" : v })}>
                        <SelectTrigger className="rounded-sm mt-1.5" data-testid="preset-account-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All mailboxes</SelectItem>
                          {companyAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.email_address}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {installTarget.preset.requires_folder && (
                      <div>
                        <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                          Target folder (needed for &ldquo;{installTarget.preset.requires_folder.name}&rdquo;)
                        </Label>
                        <Select value={installTarget.folder_id || ""} onValueChange={(v) => setInstallTarget({ ...installTarget, folder_id: v })}>
                          <SelectTrigger className="rounded-sm mt-1.5" data-testid="preset-folder-select"><SelectValue placeholder="Choose a folder…" /></SelectTrigger>
                          <SelectContent>
                            {companyFolders.length === 0 && <div className="p-2 text-xs text-muted-foreground">No folders yet — create one from the Inbox sidebar first.</div>}
                            {companyFolders.map((f) => (
                              <SelectItem key={f.id} value={f.id}>
                                <span className={`inline-block h-2 w-2 rounded-sm mr-2 align-middle bg-${f.color}-500`} />
                                {f.name} <span className="text-muted-foreground text-[10px]">({accounts.find((a) => a.id === f.account_id)?.email_address?.split("@")[0]})</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter>
                  {installTarget && <Button variant="ghost" className="rounded-sm" onClick={() => setInstallTarget(null)}>Back</Button>}
                  <Button
                    className="rounded-sm"
                    disabled={!installTarget || (installTarget.preset.requires_folder && !installTarget.folder_id)}
                    onClick={installPreset}
                    data-testid="preset-install-confirm"
                  >
                    {installTarget ? "Install rule" : "Pick a preset above"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="new-rule-button"><Plus className="h-4 w-4 mr-1.5" /> New rule</Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">Create routing rule</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Fld label="Company">
                      <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, account_id: "" })}>
                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </Fld>
                    <Fld label="Rule name">
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm" data-testid="rule-name-input" />
                    </Fld>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Fld label="Scope (blank = all mailboxes)">
                      <Select value={form.account_id || "__all__"} onValueChange={(v) => setForm({ ...form, account_id: v === "__all__" ? "" : v })}>
                        <SelectTrigger className="rounded-sm" data-testid="rule-account-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All mailboxes in company</SelectItem>
                          {companyAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.email_address}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Fld>
                    <Fld label="Match">
                      <Select value={form.match_type} onValueChange={(v) => setForm({ ...form, match_type: v })}>
                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Match ALL conditions</SelectItem>
                          <SelectItem value="any">Match ANY condition</SelectItem>
                        </SelectContent>
                      </Select>
                    </Fld>
                  </div>

                  <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                    <div>
                      <div className="text-sm font-medium">Stop processing further rules on match</div>
                      <div className="text-[11px] text-muted-foreground">Useful for exclusive filters (e.g. spam) that shouldn&apos;t be overridden.</div>
                    </div>
                    <Switch checked={form.stop_processing} onCheckedChange={(v) => setForm({ ...form, stop_processing: v })} data-testid="stop-processing-switch" />
                  </div>

                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Conditions</div>
                    <div className="space-y-2">
                      {form.conditions.map((c, i) => (
                        <div key={i} className="grid grid-cols-[1fr_110px_1.2fr_auto] gap-2 items-center" data-testid={`cond-row-${i}`}>
                          <Select value={c.field} onValueChange={(v) => updateCondition(i, "field", v)}>
                            <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                          </Select>
                          <Select value={c.op} onValueChange={(v) => updateCondition(i, "op", v)}>
                            <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{OPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select>
                          <Input value={c.value} onChange={(e) => updateCondition(i, "value", e.target.value)} className="rounded-sm h-9" placeholder="Value" />
                          <Button size="icon" variant="ghost" type="button" onClick={() => setForm({ ...form, conditions: form.conditions.filter((_, x) => x !== i) })}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      ))}
                      <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={() => setForm({ ...form, conditions: [...form.conditions, { field: "sender_email", op: "equals", value: "" }] })} data-testid="add-condition-button">
                        + Add condition
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Actions</div>
                    <div className="space-y-2">
                      {form.actions.map((a, i) => {
                        const spec = ACTIONS.find((x) => x.value === a.type);
                        return (
                          <div key={i} className="grid grid-cols-[1fr_1.5fr_auto] gap-2 items-center" data-testid={`action-row-${i}`}>
                            <Select value={a.type} onValueChange={(v) => updateAction(i, "type", v)}>
                              <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>{ACTIONS.map((ac) => <SelectItem key={ac.value} value={ac.value}>{ac.label}</SelectItem>)}</SelectContent>
                            </Select>
                            {spec?.needsFolder ? (
                              <Select value={a.value} onValueChange={(v) => updateAction(i, "value", v)}>
                                <SelectTrigger className="rounded-sm h-9" data-testid={`action-folder-${i}`}><SelectValue placeholder="Choose folder" /></SelectTrigger>
                                <SelectContent>
                                  {companyFolders.map((f) => (
                                    <SelectItem key={f.id} value={f.id}>
                                      <span className={`inline-block h-2 w-2 rounded-sm mr-2 align-middle bg-${f.color}-500`} />
                                      {f.name} <span className="text-muted-foreground text-[10px]">({accounts.find((x) => x.id === f.account_id)?.email_address?.split("@")[0]})</span>
                                    </SelectItem>
                                  ))}
                                  {companyFolders.length === 0 && <div className="p-2 text-xs text-muted-foreground">No folders. Create one from Inbox sidebar first.</div>}
                                </SelectContent>
                              </Select>
                            ) : spec?.needsDept ? (
                              <Select value={a.value} onValueChange={(v) => updateAction(i, "value", v)}>
                                <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Choose department" /></SelectTrigger>
                                <SelectContent>{companyDepts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : spec?.options ? (
                              <Select value={a.value} onValueChange={(v) => updateAction(i, "value", v)}>
                                <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Choose value" /></SelectTrigger>
                                <SelectContent>{spec.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : (
                              <Input value={a.value} onChange={(e) => updateAction(i, "value", e.target.value)} className="rounded-sm h-9" placeholder="Value (optional)" />
                            )}
                            <Button size="icon" variant="ghost" type="button" onClick={() => setForm({ ...form, actions: form.actions.filter((_, x) => x !== i) })}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        );
                      })}
                      <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={() => setForm({ ...form, actions: [...form.actions, { type: "add_tag", value: "" }] })} data-testid="add-action-button">
                        + Add action
                      </Button>
                    </div>
                  </div>
                  <DialogFooter><Button type="submit" className="rounded-sm" data-testid="rule-create-submit">Create rule</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="p-6 lg:p-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {rules.map((r) => {
          const acct = accounts.find((a) => a.id === r.account_id);
          return (
            <div key={r.id} className="border border-border bg-card rounded-sm p-5 shadow-sm" data-testid={`rule-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-8 w-8 rounded-sm bg-emerald-brand/12 text-emerald-brand grid place-items-center shrink-0"><GitPullRequestArrow className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{r.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Match {r.match_type} · {r.conditions?.length || 0} conditions · {r.actions?.length || 0} actions
                      {r.stop_processing && " · STOP"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="rounded-sm h-8 px-2 text-xs" onClick={() => runNow(r)} data-testid={`run-now-${r.id}`} title="Apply to existing emails">
                    <Play className="h-3 w-3 mr-1" /> Run
                  </Button>
                  <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`rule-toggle-${r.id}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(r.id)} data-testid={`delete-rule-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {acct ? (
                  <Badge variant="outline" className="text-[10px] font-mono uppercase rounded-sm">{acct.email_address}</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] font-mono uppercase rounded-sm text-muted-foreground">all mailboxes</Badge>
                )}
              </div>
              <div className="mt-3 space-y-1 text-xs">
                {(r.conditions || []).map((c, i) => (
                  <div key={i} className="text-muted-foreground"><span className="font-mono">{c.field}</span> <span>{c.op}</span> <span className="text-foreground font-mono">&ldquo;{c.value}&rdquo;</span></div>
                ))}
                <div className="pt-1 border-t border-border mt-2">
                  {(r.actions || []).map((a, i) => {
                    let val = a.value;
                    if (a.type === "move_to_folder") {
                      const f = folders.find((x) => x.id === a.value);
                      val = f ? f.name : "(folder)";
                    } else if (a.type === "assign_department") {
                      const d = depts.find((x) => x.id === a.value);
                      val = d ? d.name : "(department)";
                    }
                    return (
                      <div key={i} className="text-xs"><span className="text-terracotta font-mono uppercase">then</span> <span>{a.type.replace(/_/g, " ")}</span> {val && <span className="font-mono text-foreground">→ {val}</span>}</div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
        {rules.length === 0 && <div className="text-muted-foreground text-sm col-span-2 text-center py-10">No rules yet. Try installing a preset →</div>}
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
